import { FormInstance } from "antd"
import { useMemo } from "react"

import { useGetJiraCreateIssueMetadata } from "@/api/useJira"
import { Form } from "@/atoms"
import { JiraFieldKey } from "@/const/jira.constants"
import { getFieldsFromResponse } from "@/helpers/jira.helpers"
import { toDictionary } from "@/helpers/utils"

/**
 * Manages ticketing system (Jira) project and issue type selection state and metadata fetching.
 * Handles the cascading relationship where changing project resets issue type.
 *
 * @param form - Ant Design form instance
 * @returns Project/issue type state, metadata, loading states, and handlers
 */
export const useTicketingMetadata = <T extends Record<string, any>>(
  form: FormInstance<T>
) => {
  // Fetch initial metadata (projects list, URL)
  const { response: metadataResponse, isLoading: metadataIsLoading } =
    useGetJiraCreateIssueMetadata()
  const { data, metadata } = metadataResponse || {}
  const { url } = metadata || {}
  const { projects = [] } = data || {}

  // Watch form values for project and issue type
  const projectId = Form.useWatch(JiraFieldKey.Project, form) as string
  const issueTypeId = Form.useWatch(JiraFieldKey.IssueType, form) as string

  // Find selected project
  const selectedProject = projects.find(({ id }) => id === projectId)
  const issueTypesOptions = selectedProject?.issuetypes || []

  // Fetch dynamic fields based on selected project/issue type
  const { response: fieldsResponse, isLoading: fieldsIsLoading } =
    useGetJiraCreateIssueMetadata({
      projectId
    })

  // Extract fields and metadata for the selected project/issue type combination
  const fields = getFieldsFromResponse(fieldsResponse, projectId, issueTypeId)
  const fieldsMetadata = toDictionary(fields, "key")

  // Combine static fields (rendered by TicketModal) with dynamic fields (rendered by children)
  // This is used for error processing to support validation errors on static fields
  const allFields = useMemo(() => {
    const staticFields: Jira.IssueTypeField[] = [
      {
        key: "project",
        name: "Project",
        required: true,
        hasDefaultValue: false,
        schema: { type: "string" }
      },
      {
        key: "issuetype",
        name: "Issue Type",
        required: true,
        hasDefaultValue: false,
        schema: { type: "string" }
      },
      {
        key: "summary",
        name: "Summary",
        required: true,
        hasDefaultValue: false,
        schema: { type: "string" }
      },
      {
        key: "description",
        name: "Description",
        required: false,
        hasDefaultValue: false,
        schema: { type: "string" }
      }
    ]

    return [...staticFields, ...(fields || [])]
  }, [fields])

  /**
   * Handles project change and resets issue type to first option.
   * This ensures issue type is always valid for the selected project.
   */
  const handleProjectChange = (newProjectId: string) => {
    const newSelectedProject = projects.find((p) => p.id === newProjectId)
    const newIssueTypeId = newSelectedProject?.issuetypes[0]?.id
    form.setFieldsValue({
      [JiraFieldKey.IssueType]: newIssueTypeId
    } as Partial<T>)
  }

  return {
    // Project/Issue Type state
    projectId,
    issueTypeId,
    projects,
    selectedProject,
    issueTypesOptions,

    // Fields metadata
    fields,
    allFields,
    fieldsMetadata,

    // Loading states
    metadataIsLoading,
    fieldsIsLoading,

    // Metadata
    url,

    // Handlers
    handleProjectChange
  }
}
